import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {goBack} from '@/utils';
import {packageId} from '@/config';
import {getAgentRuleData} from './proxy.service';
import {AgentRuleData, CommissionItem} from './proxy.type';
import {getLevelImage, toPriceStr} from './proxy-h5.shared';
import {invitationRulesBgImage} from './proxy.variable';

type RulesTableProps = {
  titles: string[];
  rows: React.ReactNode[][];
  colClassNames?: string[];
  className?: string;
};

const titleBaseCellClass =
  'flex items-center text-xs font-medium text-[var(--proxy-main)] whitespace-pre-line';
const bodyBaseCellClass =
  'flex items-center text-xs font-medium text-[var(--proxy-second)] whitespace-pre-line';

const RuleCard = ({
  title,
  paragraphs,
  children,
  className,
  secondParagraphGapLarge = false,
}: {
  title: string;
  paragraphs: string[];
  children?: React.ReactNode;
  className?: string;
  secondParagraphGapLarge?: boolean;
}) => (
  <div
    className={`mx-3 rounded-xl border border-white bg-[rgba(255,255,255,0.8)] p-3 shadow-[0px_0px_13px_0px_rgba(0,0,0,0.17)] backdrop-blur-[56px] ${
      className || ''
    }`}>
    <div className="font-[DIN] text-[1.375rem] font-bold leading-none text-[var(--proxy-main)]">
      {title}
    </div>
    {paragraphs.map((paragraph, index) => (
      <span
        key={`${title}-${index}`}
        className={`block text-sm text-[var(--proxy-second)] ${
          index === 0 ? 'mt-2' : secondParagraphGapLarge ? 'mt-8' : 'mt-3'
        }`}>
        {paragraph}
      </span>
    ))}
    {children}
  </div>
);

const RulesTable = ({
  titles,
  rows,
  colClassNames,
  className,
}: RulesTableProps) => {
  const cols = colClassNames || titles.map(() => 'flex-1 justify-center');

  return (
    <div className={className}>
      <div className="flex h-14 items-center justify-between rounded-t-xl bg-[rgba(217,0,0,0.08)] p-3 text-xs">
        {titles.map((title, index) => (
          <div
            key={`${title}-${index}`}
            className={`${titleBaseCellClass} ${cols[index]}`}>
            {title}
          </div>
        ))}
      </div>
      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={`flex min-h-12 items-center justify-between px-3 ${
            rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#FAEEEF]'
          }`}>
          {row.map((cell, cellIndex) => (
            <div
              key={`${rowIndex}-${cellIndex}`}
              className={`${bodyBaseCellClass} ${cols[cellIndex]}`}>
              {cell}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const RulesPillTitle = ({title}: {title: string}) => (
  <div className="mt-4 flex items-center justify-center">
    <div className="h-[3.25rem] w-[16.1875rem] rounded-lg bg-[#FFD6D2] p-1 shadow-[inset_1px_1px_1px_0px_rgba(255,255,255,0.51),inset_-1px_-1px_1px_0px_rgba(255,128,128,0.46)]">
      <div className="flex h-full items-center justify-center rounded-md bg-[radial-gradient(75.4%_103.41%_at_49.8%_14.77%,#FFB4B6_0%,#FF595F_100%)] px-4 text-center text-sm font-bold text-white shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.49)]">
        {title}
      </div>
    </div>
  </div>
);

const RulesHeader = ({title}: {title: string}) => (
  <div className="sticky top-0 z-20 flex h-11 w-full flex-none items-center justify-between bg-transparent px-3">
    <button
      type="button"
      className="relative z-10 flex h-full w-4 items-center border-0 bg-transparent p-0"
      onClick={goBack}
      aria-label="Back">
      <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
        <path
          d="M10.5 2L5 7.99676L10.5 14"
          stroke="#FFFFFF"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
    <div className="pointer-events-none absolute left-0 right-0 top-0 flex h-full items-center justify-center text-base font-bold text-white">
      {title}
    </div>
    <div className="h-full w-4" />
  </div>
);

function rulesRangeStr(min: string, max: string, defaultVal?: string) {
  if (+(min || 0) === 0 || +(max || 0) === 0) {
    return `${(+(defaultVal || 0) * 100).toFixed(2)}%`;
  }
  if (+(min || 0) === +(max || 0)) {
    return `${(+(min || 0) * 100).toFixed(2)}%`;
  }
  return `${(+(min || 0) * 100).toFixed(2)}%\n~${(+(max || 0) * 100).toFixed(
    2,
  )}%`;
}

const commissionRows = (list?: CommissionItem[]) =>
  list?.map(item => [
    <span className="w-full text-center">{item.commissionLevel}</span>,
    <span className="w-full text-right">
      {rulesRangeStr(item.tier1Min, item.tier1Max, item.tier1)}
    </span>,
    <span className="w-full text-right">
      {rulesRangeStr(item.tier2Min, item.tier2Max, item.tier2)}
    </span>,
    <span className="w-full text-right">
      {rulesRangeStr(item.tier3Min, item.tier3Max, item.tier3)}
    </span>,
    <span className="w-full text-right">
      {rulesRangeStr(item.tier4Min, item.tier4Max, item.tier4)}
    </span>,
  ]) || [];

const ProxyRules = () => {
  const {t} = useTranslation();
  const [ruleData, setRuleData] = useState<AgentRuleData>();

  useEffect(() => {
    getAgentRuleData().then(data => setRuleData(data));
  }, []);

  const teamTableRows = useMemo(
    () =>
      ruleData?.agentLevelList?.map(item => [
        <img
          src={getLevelImage(item.level)}
          alt=""
          className="h-5 w-[3.875rem]"
        />,
        <span>{item.teamMember}</span>,
        <span className="w-full text-right">
          {toPriceStr(+item.teamRecharge, {fixed: 0})}
        </span>,
        <span className="w-full text-right">
          {toPriceStr(+item.teamBetting, {fixed: 0})}
        </span>,
      ]) || [],
    [ruleData?.agentLevelList],
  );

  const commissionTitle = '05';
  const differentTitle = '06';
  const lastTitle = '07';

  const commissionTitleCols = [
    'w-16 justify-start',
    'w-14 justify-end',
    'w-14 justify-end',
    'w-14 justify-end',
    'w-14 justify-end',
  ];

  const teamCols = [
    'w-16 justify-start',
    'w-14 justify-center',
    'w-16 justify-end',
    'w-20 justify-end',
  ];

  return (
    <div className="relative flex h-full min-h-full flex-col overflow-hidden bg-[#FFFAF9]">
      <img
        src={invitationRulesBgImage}
        alt=""
        className="absolute left-0 top-0 z-0 w-full"
      />
      <div className="absolute inset-0 z-10 flex flex-col">
        <RulesHeader title={t('proxy.rules.header')} />
        <div className="h-[calc(100vh-2.75rem)] overflow-y-auto">
          <div className="mb-16">
            <div className="relative mx-3 mt-2 flex min-h-[7.75rem] items-start justify-between">
              <div className="w-[13.4375rem] pt-6 opacity-0">
                <div className="text-[1.4375rem] font-bold leading-7 text-white">
                  {t('proxy.rules.title')}
                </div>
                <div className="mt-2 text-sm leading-5 text-white">
                  {t('proxy.rules.subtitle')}
                </div>
              </div>
              <div className="h-[7.5rem] w-[7.5rem] opacity-0" />
            </div>

            <RuleCard
              title="01"
              paragraphs={[t('proxy.rules.paragraph1')]}
              className=""
            />
            <RuleCard
              title="02"
              paragraphs={[t('proxy.rules.paragraph2')]}
              className="mt-2"
            />
            <RuleCard
              title="03"
              paragraphs={[t('proxy.rules.paragraph3')]}
              className="mt-2"
            />

            {packageId !== 2 && (
              <RuleCard
                title="04"
                paragraphs={[t('proxy.rules.paragraph4')]}
                className="mt-2">
                <RulesTable
                  className="mt-3"
                  titles={[
                    'Agent level',
                    'Team Member',
                    'Team Recharge',
                    'Team Betting',
                  ]}
                  rows={teamTableRows}
                  colClassNames={teamCols}
                />
              </RuleCard>
            )}

            <RuleCard
              title={commissionTitle}
              paragraphs={[t('proxy.rules.paragraph5')]}
              className="mt-2">
              <RulesTable
                className="mt-3"
                titles={['Agent level', 'Tier1', 'Tier2', 'Tier3', 'Tier4']}
                rows={commissionRows(ruleData?.refundList)}
                colClassNames={commissionTitleCols}
              />
            </RuleCard>

            {packageId !== 2 ? (
              <RuleCard
                title={differentTitle}
                paragraphs={[
                  t('proxy.rules.paragraph6-1'),
                  t('proxy.rules.paragraph6-2'),
                ]}
                className="mt-2"
                secondParagraphGapLarge>
                {ruleData?.lotteryCommissionList?.length ? (
                  <>
                    <RulesPillTitle title={t('proxy.rules.lottery')} />
                    <RulesTable
                      className="mt-3"
                      titles={[
                        'Commission Level',
                        'Tier1',
                        'Tier2',
                        'Tier3',
                        'Tier4',
                      ]}
                      rows={commissionRows(ruleData.lotteryCommissionList)}
                      colClassNames={commissionTitleCols}
                    />
                  </>
                ) : null}
                {ruleData?.scratchCommissionList?.length ? (
                  <>
                    <RulesPillTitle title={t('proxy.rules.scratch')} />
                    <RulesTable
                      className="mt-3"
                      titles={[
                        'Commission Level',
                        'Tier1',
                        'Tier2',
                        'Tier3',
                        'Tier4',
                      ]}
                      rows={commissionRows(ruleData.scratchCommissionList)}
                      colClassNames={commissionTitleCols}
                    />
                  </>
                ) : null}
                {ruleData?.casinoCommissionList?.length ? (
                  <>
                    <RulesPillTitle title={t('proxy.rules.casino')} />
                    <RulesTable
                      className="mt-3"
                      titles={[
                        'Commission Level',
                        'Tier1',
                        'Tier2',
                        'Tier3',
                        'Tier4',
                      ]}
                      rows={commissionRows(ruleData.casinoCommissionList)}
                      colClassNames={commissionTitleCols}
                    />
                  </>
                ) : null}
                {ruleData?.sportCommissionList?.length ? (
                  <>
                    <RulesPillTitle title={t('proxy.rules.sports')} />
                    <RulesTable
                      className="mt-3"
                      titles={[
                        'Commission Level',
                        'Tier1',
                        'Tier2',
                        'Tier3',
                        'Tier4',
                      ]}
                      rows={commissionRows(ruleData.sportCommissionList)}
                      colClassNames={commissionTitleCols}
                    />
                  </>
                ) : null}
              </RuleCard>
            ) : null}

            <RuleCard
              title={lastTitle}
              paragraphs={[t('proxy.rules.paragraph7')]}
              className="my-2"
            />
            <div className="h-6" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProxyRules;
